import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Activity, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import { getAllCameras, updateCameraHealth } from "../../../service/securityService";
import type { Camera, CameraHealthStatus } from "../../../types/security_types";

const SurveillanceHealth: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Camera[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await getAllCameras({ limit: 500 });
      setRows(list);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load cameras");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runCheck = async (cam: Camera) => {
    const id = cam._id || cam.id;
    if (!id) return;
    setBusyId(id);
    try {
      await new Promise((r) => setTimeout(r, 400));
      const ok =
        cam.status === "Active" &&
        cam.ipAddress &&
        /^(\d{1,3}\.){3}\d{1,3}$/.test(cam.ipAddress);
      const healthStatus: CameraHealthStatus = ok ? "Online" : "Degraded";
      await updateCameraHealth(id, { healthStatus });
      toast.success(`Health updated for ${cam.cameraName}`);
      await load();
    } catch (e) {
      console.error(e);
      toast.error("Health check failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="px-2 py-4 mt-14 min-h-screen space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/security/surveillance")}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Overview
          </button>
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Activity className="w-6 h-6 text-[#0fa9a6]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Camera health</h2>
            <p className="text-sm text-gray-600">
              Run checks to refresh health status and last check time (integrate with real VMS ping
              in production).
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load()}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex justify-center py-16">
            <SpinLoader />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#E9F5FF] text-left">
                <tr>
                  <th className="p-3 font-semibold">Camera</th>
                  <th className="p-3 font-semibold">IP</th>
                  <th className="p-3 font-semibold">Operational</th>
                  <th className="p-3 font-semibold">Health</th>
                  <th className="p-3 font-semibold">Last check</th>
                  <th className="p-3 font-semibold w-40">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((cam) => {
                  const id = cam._id || cam.id;
                  const health = cam.healthStatus || "Unknown";
                  return (
                    <tr key={id || cam.cameraId} className="hover:bg-gray-50/80">
                      <td className="p-3">
                        <div className="font-medium text-gray-900">{cam.cameraName}</div>
                        <div className="text-xs text-gray-500">{cam.cameraId}</div>
                      </td>
                      <td className="p-3 font-mono text-xs">{cam.ipAddress}</td>
                      <td className="p-3">{cam.status}</td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            health === "Online"
                              ? "bg-emerald-100 text-emerald-800"
                              : health === "Degraded"
                                ? "bg-amber-100 text-amber-900"
                                : health === "Offline"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {health}
                        </span>
                      </td>
                      <td className="p-3 text-gray-600 text-xs">
                        {cam.lastHealthCheck
                          ? new Date(cam.lastHealthCheck).toLocaleString()
                          : "—"}
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          disabled={!id || busyId === id}
                          onClick={() => runCheck(cam)}
                          className="px-3 py-1.5 rounded-full bg-[#0fa9a6] text-white text-xs font-semibold hover:bg-teal-600 disabled:opacity-50"
                        >
                          {busyId === id ? "Checking…" : "Check health"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length === 0 && (
              <p className="p-8 text-center text-gray-500 text-sm">No cameras to monitor.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SurveillanceHealth;
